<script setup lang="ts">
import { Map, TriangleAlert } from './wx-icons'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{ items: any[]; center?: { lat: number; lng: number } | null }>()
const emit = defineEmits<{ select: [item: any] }>()
const el = ref<HTMLElement | null>(null)
const error = ref('')
let map: any = null
let markerLayer: any = null

function loadSdk() {
  if (window.TMap) return Promise.resolve(window.TMap)
  const key = String(import.meta.env.VITE_TENCENT_MAP_KEY || '').trim()
  if (!key) return Promise.reject(new Error('地图 key 尚未配置'))
  return new Promise<any>((resolve, reject) => {
    const callback = `__dingTMapReady_${Date.now()}`
    ;(window as any)[callback] = () => {
      delete (window as any)[callback]
      resolve(window.TMap)
    }
    const script = document.createElement('script')
    script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${encodeURIComponent(key)}&callback=${callback}`
    script.onerror = () => reject(new Error('地图服务加载失败'))
    document.head.appendChild(script)
  })
}

function points() {
  return props.items.map((item, index) => ({
    item,
    id: String(item.id || index),
    lat: Number(item.latitude ?? item.lat),
    lng: Number(item.longitude ?? item.lng),
  })).filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
}

async function render() {
  if (!el.value) return
  try {
    const TMap = await loadSdk()
    const list = points()
    const fallback = props.center || (list[0] ? { lat: list[0].lat, lng: list[0].lng } : { lat: 24.4798, lng: 118.0894 })
    if (!map) {
      map = new TMap.Map(el.value, { center: new TMap.LatLng(fallback.lat, fallback.lng), zoom: 12, viewMode: '2D' })
    }
    if (markerLayer) markerLayer.setMap(null)
    markerLayer = new TMap.MultiMarker({
      map,
      styles: { marker: new TMap.MarkerStyle({ width: 42, height: 48, anchor: { x: 21, y: 48 }, color: '#1F3B2D' }) },
      geometries: list.map((point) => ({ id: point.id, styleId: 'marker', position: new TMap.LatLng(point.lat, point.lng), properties: { title: point.item.name || point.item.title } })),
    })
    markerLayer.on('click', (event: any) => {
      const found = list.find((point) => point.id === String(event?.geometry?.id))
      if (found) emit('select', found.item)
    })
    if (list.length > 1) {
      const bounds = new TMap.LatLngBounds()
      list.forEach((point) => bounds.extend(new TMap.LatLng(point.lat, point.lng)))
      map.fitBounds(bounds, { padding: 72 })
    }
    error.value = ''
  } catch (reason: any) {
    error.value = reason?.message || '地图暂不可用'
  }
}

onMounted(() => nextTick(render))
watch(() => [props.items, props.center], () => nextTick(render), { deep: true })
onBeforeUnmount(() => { if (map?.destroy) map.destroy() })
</script>

<template>
  <div class="map-panel">
    <div ref="el" class="map-canvas"></div>
    <div v-if="error" class="map-fallback"><TriangleAlert :size="28" /><strong>{{ error }}</strong><span>你仍可使用列表查看全部结果</span></div>
    <div v-else-if="!items.length" class="map-fallback"><Map :size="28" /><strong>当前区域暂无结果</strong></div>
  </div>
</template>
